import { useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import {
  adminSearchUsers,
  adminSetRole,
  adminShadowban,
  adminExemptDaily,
  adminSuspend,
  adminBanUser,
  adminUnbanUser,
  adminDeleteUser,
  adminRestoreUser,
} from "@/lib/api";
import { useAuth } from "@/context/AuthProvider";

type Row = {
  id: string;
  username: string;
  email: string;
  roles: string[];
  isAdmin: boolean;
  shadowBanned: boolean;
  exemptDailyLimit: boolean;
  suspendedUntil: string | null;
  createdAt: string | null;
  isBanned?: boolean;
  isDeleted?: boolean;
  deletedAt?: string | null;
};

export default function AdminUsers() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [shadow, setShadow] = useState<"" | "1" | "0">("");
  const [suspended, setSuspended] = useState<"" | "1" | "0">("");
  const [items, setItems] = useState<Row[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const canUse = !!user?.isAdmin;

  const filters = useMemo(() => ({
    q: q.trim() || undefined, // Boş string yerine undefined gönder
    role: role.trim() || undefined,
    shadowBanned: shadow === "" ? undefined : shadow === "1",
    suspended: suspended === "" ? undefined : suspended === "1",
    limit: 50,
  }), [q, role, shadow, suspended]);

  async function load(reset = false) {
    setLoading(true);
    setActionError(null);
    try {
      const r = await adminSearchUsers({
        ...filters,
        cursor: reset ? null : cursor,
      });
      // Backend returns nextBefore, so use that
      setCursor(r.nextBefore ?? r.nextCursor ?? null);
      setItems((prev) => reset ? r.items : [...prev, ...r.items]);
    } catch (e) {
      console.error(e);
      setActionError("Kullanıcı listesi alınamadı.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!canUse) return;
    // filtre değişince baştan yükle
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse, q, role, shadow, suspended]);

  if (!canUse) {
    return (
      <div style={{ padding: 24 }}>
        <h2>Admin</h2>
        <p>Bu sayfa için admin yetkisi gerekli.</p>
      </div>
    );
  }

  const nowISO = new Date().toISOString();

  const doShadow = async (row: Row, value: boolean) => {
    setBusyId(row.id);
    setActionError(null);
    try {
      await adminShadowban(row.id, value);
      setItems((prev) => prev.map((r) => r.id === row.id ? { ...r, shadowBanned: value } : r));
    } catch (e: any) {
      setActionError(`Shadowban işlemi başarısız: ${e.message || "Hata"}`);
    } finally {
      setBusyId(null);
    }
  };

  const doExempt = async (row: Row, value: boolean) => {
    setBusyId(row.id);
    setActionError(null);
    try {
      await adminExemptDaily(row.id, value);
      setItems((prev) => prev.map((r) => r.id === row.id ? { ...r, exemptDailyLimit: value } : r));
    } catch (e: any) {
      setActionError(`Exempt işlemi başarısız: ${e.message || "Hata"}`);
    } finally {
      setBusyId(null);
    }
  };

  const doSuspend = async (row: Row, days: number) => {
    setBusyId(row.id);
    setActionError(null);
    try {
      const until = days > 0 ? new Date(Date.now() + days * 86400000).toISOString() : undefined;
      await adminSuspend(row.id, until, `ui_${days}d`);
      setItems((prev) => prev.map((r) => r.id === row.id ? { ...r, suspendedUntil: until ?? null } : r));
    } catch (e: any) {
      setActionError(`Suspend işlemi başarısız: ${e.message || "Hata"}`);
    } finally {
      setBusyId(null);
    }
  };

  const doRole = async (row: Row, add?: string, remove?: string) => {
    setBusyId(row.id);
    setActionError(null);
    try {
      await adminSetRole(row.id, { add, remove });
      setItems((prev) => prev.map((r) => {
        if (r.id !== row.id) return r;
        const roles = new Set(r.roles ?? []);
        if (add) roles.add(add);
        if (remove) roles.delete(remove);
        return { ...r, roles: Array.from(roles) };
      }));
    } catch (e: any) {
      setActionError(`Rol değiştirilemedi: ${e.message || "Hata"}`);
    } finally {
      setBusyId(null);
    }
  };

  // NEW: Ban/Unban actions
  const doBan = async (row: Row) => {
    if (!window.confirm(`"${row.username}" kullanıcısını BANLAMAK istediğinize emin misiniz?\n\nBu kullanıcı giriş yapamayacak ve hiçbir içerik göremeyecek.`)) {
      return;
    }

    setBusyId(row.id);
    setActionError(null);
    try {
      await adminBanUser(row.id);
      setItems((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, isBanned: true } : r))
      );
    } catch (e: any) {
      setActionError(`Ban işlemi başarısız: ${e.message || "Hata"}`);
    } finally {
      setBusyId(null);
    }
  };

  const doUnban = async (row: Row) => {
    if (!window.confirm(`"${row.username}" kullanıcısının BANINI KALDIRMAK istediğinize emin misiniz?`)) {
      return;
    }

    setBusyId(row.id);
    setActionError(null);
    try {
      await adminUnbanUser(row.id);
      setItems((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, isBanned: false } : r))
      );
    } catch (e: any) {
      setActionError(`Unban işlemi başarısız: ${e.message || "Hata"}`);
    } finally {
      setBusyId(null);
    }
  };

  // NEW: Delete/Restore actions
  const doDelete = async (row: Row) => {
    if (!window.confirm(`"${row.username}" kullanıcısını SOFT DELETE yapmak istediğinize emin misiniz?\n\nKullanıcı giriş yapamayacak ve içerikleri görünmez olacak.\n\nBu işlem "Geri Yükle" ile geri alınabilir.`)) {
      return;
    }

    setBusyId(row.id);
    setActionError(null);
    try {
      await adminDeleteUser(row.id);
      setItems((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? { ...r, isDeleted: true, deletedAt: new Date().toISOString() }
            : r
        )
      );
    } catch (e: any) {
      setActionError(`Delete işlemi başarısız: ${e.message || "Hata"}`);
    } finally {
      setBusyId(null);
    }
  };

  const doRestore = async (row: Row) => {
    if (!window.confirm(`"${row.username}" kullanıcısını GERİ YÜKLEMEK istediğinize emin misiniz?\n\nKullanıcı tekrar giriş yapabilecek ve içerikleri görünür olacak.`)) {
      return;
    }

    setBusyId(row.id);
    setActionError(null);
    try {
      await adminRestoreUser(row.id);
      setItems((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, isDeleted: false, deletedAt: null } : r
        )
      );
    } catch (e: any) {
      setActionError(`Restore işlemi başarısız: ${e.message || "Hata"}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ marginBottom: 12 }}>Admin · Kullanıcılar</h2>

      {/* Error Display Banner */}
      {actionError && (
        <div
          style={{
            background: "#d63031",
            color: "#fff",
            padding: "12px 16px",
            borderRadius: 8,
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}
        >
          <span>⚠️ {actionError}</span>
          <button
            onClick={() => setActionError(null)}
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: 20,
              cursor: "pointer",
              padding: "0 8px",
            }}
          >
            ✕
          </button>
        </div>
      )}

      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 200px 160px 160px auto",
        gap: 8,
        marginBottom: 12,
        alignItems: "center",
      }}>
        <input
          placeholder="Ara (username/email)…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ padding: 8, borderRadius: 8, border: "1px solid var(--muted)", background: "var(--bg2)", color: "var(--text)" }}
        />
        <input
          placeholder="Role (örn: moderator)"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          style={{ padding: 8, borderRadius: 8, border: "1px solid var(--muted)", background: "var(--bg2)", color: "var(--text)" }}
        />
        <select value={shadow} onChange={(e) => setShadow(e.target.value as any)} style={{ padding: 8, borderRadius: 8 }}>
          <option value="">Shadowban (hepsi)</option>
          <option value="1">Sadece shadowban</option>
          <option value="0">Shadowban değil</option>
        </select>
        <select value={suspended} onChange={(e) => setSuspended(e.target.value as any)} style={{ padding: 8, borderRadius: 8 }}>
          <option value="">Suspend (hepsi)</option>
          <option value="1">Sadece askıda</option>
          <option value="0">Askıda olmayan</option>
        </select>

        <div style={{ textAlign: "right" }}>
          <button disabled={loading} onClick={() => load(true)}>Yenile</button>
        </div>
      </div>

      <div style={{ width: "100%", overflowX: "auto", border: "1px solid var(--muted)", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#0f0f0f" }}>
              <th style={th}>Kullanıcı</th>
              <th style={th}>Email</th>
              <th style={th}>Roller</th>
              <th style={th}>Durum</th>
              <th style={th}>Aksiyonlar</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => {
              const isSusp = r.suspendedUntil ? (r.suspendedUntil > nowISO) : false;
              const isBanned = r.isBanned === true;
              const isDeleted = r.isDeleted === true;
              const busy = busyId === r.id;

              // Row styling for deleted/banned
              const rowStyle: CSSProperties = {
                opacity: isDeleted ? 0.6 : 1,
                background: (isBanned || isDeleted) ? "#1a1010" : "transparent",
              };

              return (
                <tr key={r.id} style={rowStyle}>
                  <td style={td}><b>{r.username}</b><div style={{ opacity: 0.7, fontSize: 12 }}>{r.id}</div></td>
                  <td style={td}>{r.email}</td>
                  <td style={td}>{r.roles?.join(", ") || "-"}</td>
                  <td style={td}>
                    {/* New Ban/Delete Badges */}
                    {isBanned && <span className="chip" style={{ background: "#d63031", color: "#fff", fontWeight: "bold" }}>⛔ BANLI</span>}{" "}
                    {isDeleted && <span className="chip" style={{ background: "#555", color: "#999", fontWeight: "bold" }}>🗑️ SİLİNMİŞ</span>}{" "}
                    
                    {/* Existing Badges */}
                    {r.isAdmin && <span className="chip gold">admin</span>}{" "}
                    {r.shadowBanned && <span className="chip">shadow</span>}{" "}
                    {r.exemptDailyLimit && <span className="chip">exempt</span>}{" "}
                    {isSusp && <span className="chip">suspended</span>}
                  </td>
                  <td style={td}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {/* Existing Actions */}
                      <button disabled={busy} onClick={() => doShadow(r, !r.shadowBanned)} title={r.shadowBanned ? "Shadowban kaldır" : "Shadowban uygula"}>
                        {r.shadowBanned ? "Unshadow" : "Shadow"}
                      </button>
                      <button disabled={busy} onClick={() => doExempt(r, !r.exemptDailyLimit)} title={r.exemptDailyLimit ? "Daily limit muafiyetini kaldır" : "Daily limit muafiyeti ver"}>
                        {r.exemptDailyLimit ? "Unexempt" : "ExemptDaily"}
                      </button>
                      <button disabled={busy} onClick={() => doSuspend(r, isSusp ? 0 : 7)} title={isSusp ? "Suspend kaldır" : "7 gün suspend"}>
                        {isSusp ? "Unsuspend" : "Suspend 7d"}
                      </button>
                      <button disabled={busy} onClick={() => doRole(r, "moderator", undefined)} title="Moderator rolü ekle">
                        +moderator
                      </button>
                      <button disabled={busy} onClick={() => doRole(r, undefined, "moderator")} title="Moderator rolünü kaldır">
                        -moderator
                      </button>

                      {/* New Ban/Unban Actions */}
                      {isBanned ? (
                        <button
                          disabled={busy}
                          onClick={() => doUnban(r)}
                          style={{ background: "#555", color: "#fff" }}
                          title="Banı kaldır"
                        >
                          Banı Kaldır
                        </button>
                      ) : (
                        <button
                          disabled={busy}
                          onClick={() => doBan(r)}
                          style={{ background: "#d63031", color: "#fff" }}
                          title="Kullanıcıyı kalıcı banla"
                        >
                          Banla
                        </button>
                      )}

                      {/* New Delete/Restore Actions */}
                      {isDeleted ? (
                        <button
                          disabled={busy}
                          onClick={() => doRestore(r)}
                          style={{ background: "#00d084", color: "#fff" }}
                          title="Kullanıcıyı geri yükle"
                        >
                          Geri Yükle
                        </button>
                      ) : (
                        <button
                          disabled={busy}
                          onClick={() => doDelete(r)}
                          style={{ background: "#555", color: "#fff" }}
                          title="Kullanıcıyı soft delete yap"
                        >
                          Sil
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!items.length && (
              <tr><td colSpan={5} style={{ padding: 16, textAlign: "center", opacity: 0.7 }}>
                Sonuç yok
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
        <button disabled={loading || !cursor} onClick={() => load(false)}>
          Daha fazla yükle
        </button>
      </div>
    </div>
  );
}

const th: CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "1px solid var(--muted)",
  whiteSpace: "nowrap",
};

const td: CSSProperties = {
  padding: "8px 12px",
  borderBottom: "1px solid #1b1b1b",
};
